import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Grid,
  IconButton,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Eye } from 'lucide-react'
import { AppCard } from '@/shared'
import {
  formatBloodCollectionDateTime,
  getHistoryStatusStyle,
  translateHistoryStatus,
} from '../lib/history-utils'
import type { IBloodCollectionHistoryItem } from '../model/blood-collection.type'

interface BloodCollectionHistoryCardProps {
  history?: IBloodCollectionHistoryItem[]
  isLoading?: boolean
}

export function BloodCollectionHistoryCard({
  history,
  isLoading = false,
}: BloodCollectionHistoryCardProps) {
  const { t } = useTranslation()

  const historyColumns = useMemo(
    () => [
      t('laboratory_research.blood_collection.history_col_date'),
      t('laboratory_research.blood_collection.history_col_doctor'),
      t('laboratory_research.blood_collection.history_col_assistant'),
      t('laboratory_research.blood_collection.history_col_status'),
      '',
    ],
    [t],
  )

  const rows = history?.length ? history : []

  return (
    <AppCard.Root>
      <AppCard.Body p={5}>
        <Text fontWeight="semibold" fontSize="md" mb={4}>
          {t('laboratory_research.blood_collection.history_title')}
        </Text>

        <Grid
          templateColumns="1fr 1fr 1fr 120px 40px"
          gap={2}
          pb={2}
          borderBottomWidth="1px"
          borderColor="border"
          display={{ base: 'none', md: 'grid' }}
        >
          {historyColumns.map((col, index) => (
            <Text
              key={`${col}-${index}`}
              fontSize="xs"
              color="fg.muted"
              fontWeight="semibold"
            >
              {col}
            </Text>
          ))}
        </Grid>

        <VStack gap={0} align="stretch">
          {isLoading ? (
            <Skeleton h="80px" />
          ) : rows.length === 0 ? (
            <Text fontSize="sm" color="fg.muted" py={4}>
              {t('laboratory_research.blood_collection.history_empty')}
            </Text>
          ) : (
            rows.map((row) => {
              const statusStyle = getHistoryStatusStyle(row.status)
              return (
                <Grid
                  key={row.id}
                  templateColumns={{
                    base: '1fr',
                    md: '1fr 1fr 1fr 120px 40px',
                  }}
                  gap={2}
                  py={3}
                  borderBottomWidth="1px"
                  borderColor="border.muted"
                  _last={{ borderBottom: 'none' }}
                  alignItems="center"
                >
                  <Text fontSize="sm" color="fg.muted">
                    {formatBloodCollectionDateTime(row.collection_datetime)}
                  </Text>
                  <Text fontSize="sm">{row.screening_doctor_fio}</Text>
                  <Text fontSize="sm">{row.nurse_fio}</Text>
                  <Badge
                    px={2}
                    py={0.5}
                    borderRadius="md"
                    fontSize="11px"
                    fontWeight="medium"
                    bg={statusStyle.bg}
                    color={statusStyle.color}
                    w="fit-content"
                  >
                    {translateHistoryStatus(t, row.status)}
                  </Badge>
                </Grid>
              )
            })
          )}
        </VStack>
      </AppCard.Body>
    </AppCard.Root>
  )
}
